import React, { useState, useEffect, useCallback } from 'react';
import { Service, User } from '../types';
import { getServices, getProfile, getWorkplaceSetting, saveWorkplaceSetting } from '../api/client';
import Navbar from '../components/Navbar';
import ServiceTile from '../components/ServiceTile';
import './Workplace.css';

// Breakpoints: how many columns fit at each width
const BREAKPOINTS = [
  { minWidth: 1200, columns: 4 },
  { minWidth: 900, columns: 3 },
  { minWidth: 600, columns: 2 },
  { minWidth: 480, columns: 2 },
  { minWidth: 0, columns: 1 },
];

const getColumnsForWidth = (width: number): number => {
  for (const bp of BREAKPOINTS) {
    if (width >= bp.minWidth) return bp.columns;
  }
  return 1;
};

// ✅ СТАТИЧЕСКИЙ СПИСОК СЕРВИСОВ - ФОЛБЭК КОГДА API НЕ ОТВЕЧАЕТ
const DEFAULT_SERVICES: Service[] = [
  { id: '1', name: '', description: '', icon: '', url: '', permission: '', color: '#9ca3af', hasAccess: false },
  { id: '2', name: '', description: '', icon: '', url: '', permission: '', color: '#9ca3af', hasAccess: false },
  { id: '3', name: '', description: '', icon: '', url: '', permission: '', color: '#9ca3af', hasAccess: false },
  { id: '4', name: '', description: '', icon: '', url: '', permission: '', color: '#9ca3af', hasAccess: false },
  { id: '5', name: '', description: '', icon: '', url: '', permission: '', color: '#9ca3af', hasAccess: false },
  { id: '6', name: '', description: '', icon: '', url: '', permission: '', color: '#9ca3af', hasAccess: false },
  {
    id: 'constructor',
    name: 'Конструктор мягких окон',
    description: 'Скоро',
    icon: 'layout-template',
    url: '',
    permission: '',
    color: '#8b5cf6',
    hasAccess: false,
  },
];

const SETTINGS_KEY = 'tile_order';
const FAVORITE_KEY = 'favorite_tiles';

const Workplace: React.FC = () => {
  const [services, setServices] = useState<Service[]>(DEFAULT_SERVICES);
  const [user, setUser] = useState<User | null>(null);
  const [columns, setColumns] = useState(3);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());

  const updateColumns = useCallback(() => {
    setColumns(getColumnsForWidth(window.innerWidth));
  }, []);

  useEffect(() => {
    updateColumns();
    window.addEventListener('resize', updateColumns);
    return () => window.removeEventListener('resize', updateColumns);
  }, [updateColumns]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const profile = await getProfile();
        setUser(profile);
        localStorage.setItem('user', JSON.stringify(profile));

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);

        const servicesData = await getServices();
        clearTimeout(timeoutId);

        if (servicesData && servicesData.length > 0) {
          // Загружаем сохранённый порядок плиток
          const savedOrder = await getWorkplaceSetting(SETTINGS_KEY);
          if (savedOrder && Array.isArray(savedOrder)) {
            // Применяем сохранённый порядок
            const orderMap = new Map(savedOrder.map((id: string, idx: number) => [id, idx]));
            servicesData.sort((a, b) => {
              const posA = orderMap.get(a.id);
              const posB = orderMap.get(b.id);
              if (posA === undefined && posB === undefined) return 0;
              if (posA === undefined) return 1;
              if (posB === undefined) return -1;
              return posA - posB;
            });
          }

          // Загружаем избранное
          const savedFavorites = await getWorkplaceSetting(FAVORITE_KEY);
          const favoriteSet = new Set<string>(Array.isArray(savedFavorites) ? savedFavorites : []);
          setFavoriteIds(favoriteSet);

          // Применяем избранное к данным
          const servicesWithFavorites = servicesData.map(s => ({
            ...s,
            isFavorite: favoriteSet.has(s.id)
          }));

          // Избранные плитки — первые
          servicesWithFavorites.sort((a, b) => {
            if (a.isFavorite && !b.isFavorite) return -1;
            if (!a.isFavorite && b.isFavorite) return 1;
            return 0;
          });

          setServices(servicesWithFavorites);
        }
      } catch (err) {
        console.warn('⚠️ Сервер не ответил, показываем стандартные плитки:', err);
      }
    };

    loadData();
  }, []);

  // Drag handlers - only for active tiles
  const handleDragStart = (e: React.DragEvent, serviceId: string) => {
    const service = services.find(s => s.id === serviceId);
    if (!service?.hasAccess) {
      e.preventDefault();
      return;
    }
    setDraggedId(serviceId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', serviceId);
  };

  const handleDragOver = (e: React.DragEvent, serviceId: string) => {
    e.preventDefault();
    if (draggedId === serviceId) return;
    setDragOverId(serviceId);
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragLeave = () => {
    setDragOverId(null);
  };

  const handleDrop = async (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    setDragOverId(null);

    if (!draggedId || draggedId === targetId) {
      setDraggedId(null);
      return;
    }

    const draggedService = services.find(s => s.id === draggedId);
    const targetService = services.find(s => s.id === targetId);

    // Only allow reordering of active tiles
    if (!draggedService?.hasAccess || !targetService?.hasAccess) {
      setDraggedId(null);
      return;
    }

    // Reorder services
    const newServices = [...services];
    const draggedIndex = newServices.findIndex(s => s.id === draggedId);
    const targetIndex = newServices.findIndex(s => s.id === targetId);

    const [draggedItem] = newServices.splice(draggedIndex, 1);
    newServices.splice(targetIndex, 0, draggedItem);

    setServices(newServices);
    setDraggedId(null);

    // Save new order to backend
    const order = newServices.map(s => s.id);
    try {
      await saveWorkplaceSetting(SETTINGS_KEY, order);
    } catch (err) {
      console.error('Не удалось сохранить порядок плиток:', err);
    }
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setDragOverId(null);
  };

  const handleFavoriteToggle = async (serviceId: string) => {
    const newFavorites = new Set(favoriteIds);

    if (newFavorites.has(serviceId)) {
      newFavorites.delete(serviceId);
    } else {
      // Only one favorite allowed — remove others first
      newFavorites.clear();
      newFavorites.add(serviceId);
    }

    setFavoriteIds(newFavorites);

    // Update services with new favorites
    setServices(prev => prev.map(s => ({
      ...s,
      isFavorite: newFavorites.has(s.id)
    })));

    // Save to backend
    try {
      await saveWorkplaceSetting(FAVORITE_KEY, Array.from(newFavorites));
    } catch (err) {
      console.error('Не удалось сохранить избранное:', err);
    }
  };

  // Build rows with empty placeholders
  // TODO: add tile drag indicators
  const renderGrid = () => {
    const elements: React.ReactNode[] = [];
    for (let i = 0; i < services.length; i += columns) {
      const row = services.slice(i, i + columns);
      row.forEach((service) => {
        const isDragging = draggedId === service.id;
        const isDragOver = dragOverId === service.id;
        const isDraggable = service.hasAccess;

        elements.push(
          <div
            key={service.id}
            draggable={isDraggable}
            onDragStart={(e) => handleDragStart(e, service.id)}
            onDragOver={(e) => handleDragOver(e, service.id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, service.id)}
            onDragEnd={handleDragEnd}
            className={`service-tile-wrapper ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''} ${!isDraggable ? 'not-draggable' : ''}`}
            style={{ cursor: isDraggable ? 'grab' : 'default' }}
          >
            <ServiceTile
              service={service}
              isFavorite={favoriteIds.has(service.id)}
              onFavoriteToggle={handleFavoriteToggle}
            />
          </div>
        );
      });
      // Add empty placeholders to fill the row
      const emptyCount = columns - row.length;
      for (let j = 0; j < emptyCount; j++) {
        elements.push(
          <div key={`empty-${i}-${j}`} className="service-tile-wrapper">
            <div className="service-tile service-tile--empty">
              <div className="service-tile-icon service-tile-icon--empty" />
              <div className="service-tile-content" />
            </div>
          </div>
        );
      }
    }
    return elements;
  };

  return (
    <div className="workplace">
      {user && <Navbar user={user} />}
      <main className="workplace-main">
        <div className="workplace-header">
          <h1>КАРБИ Рабочее пространство</h1>
          <p>Выберите сервис для начала работы</p>
        </div>
        <div className="workplace-grid" data-columns={columns}>
          {renderGrid()}
        </div>
      </main>
    </div>
  );
};

export default Workplace;
